/**
 * ConclusionForm Component
 * Form for ASO conclusion
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Chip,
  Autocomplete,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { parseISO } from 'date-fns';

import {
  CompleteConsultationData,
  MedicalOpinion,
  getMedicalOpinionLabel,
  getMedicalOpinionColor,
} from '../../../types/medicalRecord';
import type { AppointmentExam } from '../../../types/appointmentExam';
import {
  APPOINTMENT_EXAM_RESULT_LABELS,
  APPOINTMENT_EXAM_RESULT_COLORS,
} from '../../../types/appointmentExam';
import { DataTable, type DataTableColumn } from '../../../components';

const examColumns: DataTableColumn<AppointmentExam>[] = [
  {
    id: 'exam',
    label: 'Exame',
    primary: true,
    render: (exam) => (
      <Typography variant="body2">
        {exam.occupationalExam?.examName ?? `Exame #${exam.occupationalExamId}`}
      </Typography>
    ),
  },
  {
    id: 'result',
    label: 'Resultado',
    render: (exam) =>
      exam.result ? (
        <Chip
          label={APPOINTMENT_EXAM_RESULT_LABELS[exam.result]}
          color={APPOINTMENT_EXAM_RESULT_COLORS[exam.result]}
          size="small"
          variant="outlined"
        />
      ) : (
        <Chip label="Não realizado" size="small" color="default" variant="outlined" />
      ),
  },
  {
    id: 'observations',
    label: 'Observações',
    render: (exam) => (
      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
        {exam.resultDetails || exam.observations || '—'}
      </Typography>
    ),
  },
];

interface ConclusionFormProps {
  data: CompleteConsultationData;
  onChange: (data: CompleteConsultationData) => void;
  disabled?: boolean;
  isCompleted?: boolean;
  appointmentExams?: AppointmentExam[];
}

// Common CID codes for occupational health
const COMMON_CID_CODES = [
  { code: 'Z00.0', description: 'Exame médico geral' },
  { code: 'Z02.1', description: 'Exame pré-emprego' },
  { code: 'Z02.0', description: 'Exame para admissão em instituição educacional' },
  { code: 'Z10.0', description: 'Exame de saúde ocupacional' },
  { code: 'J00', description: 'Nasofaringite aguda (resfriado comum)' },
  { code: 'J06.9', description: 'Infecção aguda das vias aéreas superiores' },
  { code: 'M54.5', description: 'Dor lombar baixa' },
  { code: 'M54.2', description: 'Cervicalgia' },
  { code: 'G43.9', description: 'Enxaqueca' },
  { code: 'I10', description: 'Hipertensão essencial (primária)' },
  { code: 'E11.9', description: 'Diabetes mellitus tipo 2' },
  { code: 'F32.0', description: 'Episódio depressivo leve' },
  { code: 'F41.1', description: 'Ansiedade generalizada' },
];

export const ConclusionForm: React.FC<ConclusionFormProps> = ({
  data,
  onChange,
  disabled = false,
  isCompleted: _isCompleted = false,
  appointmentExams = [],
}) => {
  const handleOpinionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, medicalOpinion: event.target.value as MedicalOpinion });
  };

  const handleTextChange =
    (field: keyof CompleteConsultationData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...data, [field]: event.target.value ?? null });
    };

  const handleDateChange = (date: Date | null) => {
    onChange({ ...data, returnDate: date ? date.toISOString().split('T')[0] : null });
  };

  const handleCidChange = (_: React.SyntheticEvent, value: string[]) => {
    onChange({ ...data, cidCodes: value.length > 0 ? value : null });
  };

  return (
    <Box>
      {/* Exam Results Summary */}
      {appointmentExams.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Resumo dos Exames
          </Typography>
          <DataTable
            columns={examColumns}
            rows={appointmentExams}
            getRowId={(exam) => exam.id}
            emptyState={{
              title: 'Nenhum exame registrado',
              description:
                'O resumo mostra os exames deste atendimento e os resultados lançados durante a consulta.',
            }}
          />
          <Divider sx={{ mt: 2, mb: 1 }} />
        </Box>
      )}

      {/* Parecer Médico */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Parecer Médico (ASO)
      </Typography>
      <FormControl component="fieldset" disabled={disabled} sx={{ mb: 3 }}>
        <FormLabel component="legend">Conclusão</FormLabel>
        <RadioGroup value={data.medicalOpinion ?? ''} onChange={handleOpinionChange} row>
          {Object.values(MedicalOpinion).map((opinion) => (
            <FormControlLabel
              key={opinion}
              value={opinion}
              control={<Radio />}
              label={
                <Chip
                  label={getMedicalOpinionLabel(opinion)}
                  color={getMedicalOpinionColor(opinion)}
                  size="small"
                  variant={data.medicalOpinion === opinion ? 'filled' : 'outlined'}
                />
              }
            />
          ))}
        </RadioGroup>
      </FormControl>
      {/* Restrições (obrigatório se APTO COM RESTRIÇÕES) */}
      {data.medicalOpinion === MedicalOpinion.APT_WITH_RESTRICTIONS && (
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Restrições *"
          placeholder="Descreva as restrições do trabalhador..."
          value={data.restrictions ?? ''}
          onChange={handleTextChange('restrictions')}
          disabled={disabled}
          required
          error={!data.restrictions && data.medicalOpinion === MedicalOpinion.APT_WITH_RESTRICTIONS}
          helperText={
            !data.restrictions && data.medicalOpinion === MedicalOpinion.APT_WITH_RESTRICTIONS
              ? 'Restrições são obrigatórias quando o parecer é APTO COM RESTRIÇÕES'
              : ''
          }
          sx={{ mb: 3 }}
        />
      )}
      {/* Recomendações */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Recomendações"
        placeholder="Recomendações adicionais..."
        value={data.recommendations ?? ''}
        onChange={handleTextChange('recommendations')}
        disabled={disabled}
        sx={{ mb: 3 }}
      />
      <Grid container spacing={2}>
        {/* Data de Retorno */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data de Retorno"
              value={data.returnDate ? parseISO(data.returnDate) : null}
              onChange={handleDateChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Para exames periódicos ou de retorno',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* CID-10 */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Autocomplete
            multiple
            freeSolo
            options={COMMON_CID_CODES.map((c) => c.code)}
            value={data.cidCodes ?? []}
            onChange={handleCidChange}
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const cidInfo = COMMON_CID_CODES.find((c) => c.code === option);
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    title={cidInfo?.description}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Códigos CID-10"
                placeholder="Digite ou selecione..."
                helperText="Opcional - códigos de diagnóstico"
              />
            )}
            renderOption={(props, option) => {
              const cidInfo = COMMON_CID_CODES.find((c) => c.code === option);
              return (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option}
                    </Typography>
                    {cidInfo && (
                      <Typography variant="caption" color="text.secondary">
                        {cidInfo.description}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConclusionForm;
